import { useMutation, useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { countBy, difference, isEqual, keyBy, map, omit, reduce, uniq, without } from "lodash";
import { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Badge, Tag, Popover, Switch, Modal } from "antd";
import { isDefined } from "@clipboard-health/util-ts";

import { ActionContainer } from "../../../components/actionContainer";
import { Colors } from "../../../utils/colors";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { fetchFacilities } from "./api";
import { DELETE_MAPPINGS, GET_MAPPINGS } from "./gql";
import { MAPPING_LEVELS, WORKER_AND_WORKPLACE_DATA_STALE_TIME_IN_MS } from "../constants";
import RequirementContext from "../context";
import { searchFilter } from "../../../utils/filtering";
import { ShowError } from "../../../components/error";
import { unwindLevels } from "../helper";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { useGetQualifications } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { useGetWorkplaceTypes } from "@src/appV2/Admin/WorkplaceTypes/api/useGetWorkplaceTypes";
import { Qualification } from "@src/appV2/Admin/Qualifications/types";
import { WorkplaceType } from "@src/appV2/Admin/WorkplaceTypes/types";
import { EditRequirementMappingDialog } from "@src/appV2/RequirementMapping/EditRequirementMappingDialog";
import { Domain } from "@src/appV2/RequirementMapping/types";
import {
  REQUIREMENT_MAPPING_RESOURCE,
  RequirementMapping,
} from "@src/appV2/RequirementMapping/types";
import { REQUIREMENT_RESOURCE } from "@src/appV2/Requirement/constants";

interface MappingTableProps {
  hcfId?: string;
}

export function MappingsTable(props: MappingTableProps) {
  const { hcfId } = props;
  const [mappings, setMappings] = useState([]);
  const [mappingCounts, setMappingCounts] = useState({});
  const [facilities, setFacilities] = useState({});
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mappingToDelete, setMappingToDelete] = useState<any>(null);
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { adminProfile } = useSelector((state: any) => state.session || {});
  /* @ts-expect-error FIXME this wrong type */
  const { filter, setFilter, setMappingToEdit } = useContext(RequirementContext);
  const { loading, error, data, refetch } = useQuery(GET_MAPPINGS, {
    variables: { hcfId, levels: unwindLevels(filter) },
  });
  const [deleteMapping, { loading: isDeleting }] = useMutation(DELETE_MAPPINGS, {
    onCompleted: () => {
      showSuccessToast("Mapping deleted successfully");
      void refetch();
    },
    onError: () => {
      showErrorToast("Failed to delete mapping");
    },
  });

  const { data: workplaceTypesData, isLoading: isLoadingWorkplaceTypes } = useGetWorkplaceTypes({
    staleTime: WORKER_AND_WORKPLACE_DATA_STALE_TIME_IN_MS,
  });

  const { data: qualificationsData, isLoading: isLoadingQualifications } = useGetQualifications(
    undefined,
    {
      staleTime: WORKER_AND_WORKPLACE_DATA_STALE_TIME_IN_MS,
    }
  );

  const workplaceTypesByName = useMemo(() => {
    const items = workplaceTypesData?.data ?? [];
    return new Map(items.map((workplaceType) => [workplaceType.attributes.name, workplaceType]));
  }, [workplaceTypesData]);

  const qualificationsByName = useMemo(() => {
    const items = qualificationsData?.data ?? [];
    return new Map(items.map((qualification) => [qualification.attributes.name, qualification]));
  }, [qualificationsData]);

  useEffect(() => {
    updateState();
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const updateState = () => {
    if (!data) {
      return;
    }

    const counts = countBy(
      data.mappings,
      (mapping) =>
        // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ GLOBAL: "GLOBAL"; HCF: "HCF"; }'.
        ({
          [MAPPING_LEVELS.GLOBAL]: MAPPING_LEVELS.GLOBAL,
          [MAPPING_LEVELS.HCF]: MAPPING_LEVELS.HCF,
        })[mapping.level] || [MAPPING_LEVELS.LOCATION]
    );
    setMappings(data.mappings);
    setMappingCounts(counts);
    void getFacilities();
  };

  const getFacilities = async () => {
    const hcfIds = reduce(
      data.mappings,
      (res, mapping) => {
        if (mapping.level === MAPPING_LEVELS.HCF) {
          res.push(...mapping.requiredBy);
        }
        return res;
      },
      hcfId ? [hcfId] : []
    );
    try {
      const res = await fetchFacilities({ hcfIds: uniq(hcfIds) }, "userId name");
      setFacilities(keyBy(res, "userId"));
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-shadow, @typescript-eslint/no-unused-vars
    } catch (error) {
      showErrorToast("Couldn't fetch workplaces.");
    }
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'value' implicitly has an 'any' type.
  const toggleFilter = (value) => () => {
    const newFilter = without(filter, value);
    if (!filter.includes(value)) {
      newFilter.push(value);
    }
    setFilter(newFilter);
  };

  // FIXME: Automatically disabled during ESLint config update
  // @ts-expect-error TODO(typecheck-unify): TS6133,TS7006 'editMapping' is declared but its value is never read.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const editMapping = (mapping) => () => {
    setMappingToEdit(mapping);
  };

  const handleDeleteMapping = () => {
    if (mappingToDelete) {
      void deleteMapping({ variables: { _id: mappingToDelete._id } });
      setMappingToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setMappingToDelete(null);
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'list' implicitly has an 'any' type.
  const renderRequiredBy = (list, record) => {
    // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'item' implicitly has an 'any' type.
    return list.map((item) => (
      <Tag key={item}>
        {record.level === MAPPING_LEVELS.HCF ? (facilities as any)[item]?.name : item}
      </Tag>
    ));
  };

  const renderType = (all: Map<string, Qualification | WorkplaceType>, selected: string[]) => {
    if (!difference(Array.from(all.keys()), selected).length) {
      return "All";
    }

    return selected.map((value) => (
      <Tag key={value}>{all.get(value)?.attributes.displayLabel ?? value}</Tag>
    ));
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'type' implicitly has an 'any' type.
  const renderVisible = (type) =>
    // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter '_' implicitly has an 'any' type.
    function (_, record) {
      return <Switch size="small" checked={record[type]} disabled={true} />;
    };

  const filterList = [
    {
      value: MAPPING_LEVELS.GLOBAL,
      displayValue: "Required by CB",
    },
    {
      value: MAPPING_LEVELS.LOCATION,
      displayValue: "Required by Location",
    },
    {
      value: MAPPING_LEVELS.HCF,
      displayValue: "Required by Workplace",
    },
  ];

  const filterButtons = filterList.map((singleFilter) => (
    <Button
      key={singleFilter.value}
      ghost={!filter.includes(singleFilter.value)}
      disabled={loading}
      onClick={toggleFilter(singleFilter.value)}
      style={{ margin: "20px", marginLeft: "0px" }}
      type="primary"
    >
      <Badge
        showZero={true}
        count={(mappingCounts as any)[singleFilter.value] || "0"}
        style={{ backgroundColor: Colors.grey }}
      />
      <span style={{ paddingLeft: "10px" }}>{singleFilter.displayValue}</span>
    </Button>
  ));

  const levelFilters = map(omit(MAPPING_LEVELS, ["LOCATION"]), (level) => ({
    text: level,
    value: level,
  }));
  const domainFilters = map(Object.values(Domain), (domain) => ({
    text: domain,
    value: domain,
  }));
  const requiredForBookingFilter = [
    { text: "Required for booking", value: true },
    { text: "Not required for booking", value: false },
  ];
  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'level' implicitly has an 'any' type.
  const onLevelFilter = (level, mapping) => mapping.level === level;
  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'domain' implicitly has an 'any' type.
  const onDomainFilter = (domain, mapping) => mapping.domain === domain;
  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'hcfType' implicitly has an 'any' type.
  const onHcfTypeFilter = (hcfType, mapping) => mapping.hcfType.includes(hcfType);
  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'hcpType' implicitly has an 'any' type.
  const onHcpTypeFilter = (hcpType, mapping) => mapping.hcpType.includes(hcpType);
  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'input' implicitly has an 'any' type.
  const onRequiredByFilter = (input, mapping) => {
    if (mapping.level !== MAPPING_LEVELS.HCF) {
      return mapping.requiredBy.join(" ").includes(input);
    }

    return (
      mapping.requiredBy
        // FIXME: Automatically disabled during ESLint config update
        // @ts-expect-error TODO(typecheck-unify): TS7006,TS7053 Parameter 'hcfId' implicitly has an 'any' type.
        // eslint-disable-next-line @typescript-eslint/no-shadow
        .map((hcfId) => (facilities[hcfId]?.name || "").toLowerCase())
        .join(" ")
        .includes(input.toLowerCase())
    );
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'type' implicitly has an 'any' type.
  const onRequiredForBookingFilter = (type) => (value, record) => record[type] === value;

  const hasEditPermission = adminProfile?.roles?.includes(EMPLOYEE_ROLES.DOCUMENT_CREATION);

  const columns = [
    {
      title: "Name",
      dataIndex: ["requirement", "name"],
      // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'name' implicitly has an 'any' type.
      render: (name, mapping) => (
        <div
          style={{
            /* @ts-expect-error FIXME this wrong type */
            ma: "break-word",
            wordBreak: "break-word",
          }}
        >
          {`${name} `}
          <Popover content={`Valid for ${mapping.validity} days`}>
            <Tag color="warning" visible={!!mapping.validity}>
              {mapping.validity}
            </Tag>
          </Popover>
        </div>
      ),
      ...searchFilter("requirement.name", "name"),
      width: "200px",
    },
    {
      title: "Domain",
      dataIndex: "domain",
      filters: domainFilters,
      onFilter: onDomainFilter,
      // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'domain' implicitly has an 'any' type.
      render: (domain) => <div>{domain}</div>,
    },
    {
      title: "Level",
      dataIndex: "level",
      filters: levelFilters,
      onFilter: onLevelFilter,
      // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'level' implicitly has an 'any' type.
      render: (level) => <div>{level}</div>,
    },
    {
      title: "Required by",
      dataIndex: "requiredBy",
      render: renderRequiredBy,
      ...searchFilter("requiredBy", "required by", onRequiredByFilter),
      width: "300",
    },
    {
      title: "HCF Types",
      dataIndex: "hcfType",
      filters: Array.from(workplaceTypesByName.values()).map(({ attributes }) => ({
        text: attributes.displayLabel,
        value: attributes.name,
      })),
      onFilter: onHcfTypeFilter,
      render: (selected: string[]) => renderType(workplaceTypesByName, selected),
      width: "300",
    },
    {
      title: "HCP Types",
      dataIndex: "hcpType",
      filters: Array.from(qualificationsByName.values()).map(({ attributes }) => ({
        text: attributes.displayLabel,
        value: attributes.name,
      })),
      onFilter: onHcpTypeFilter,
      render: (selected: string[]) => renderType(qualificationsByName, selected),
      width: "300",
    },
    {
      title: "Required For Booking",
      key: "types",
      filterMultiple: false,
      filters: requiredForBookingFilter,
      onFilter: onRequiredForBookingFilter("requiredForBooking"),
      render: renderVisible("requiredForBooking"),
      width: "200",
    },
    ...(!hasEditPermission
      ? []
      : [
          {
            title: "Edit",
            // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter '_' implicitly has an 'any' type.
            render: (_, mapping) => {
              const cannotEdit =
                hcfId &&
                (mapping.level !== MAPPING_LEVELS.HCF || !isEqual(mapping.requiredBy, [hcfId]));
              if (cannotEdit) {
                return null;
              }

              const requirementMapping: RequirementMapping = {
                id: mapping._id,
                type: REQUIREMENT_MAPPING_RESOURCE,
                attributes: {
                  level: mapping.level,
                  qualifications: mapping.hcpType,
                  workplaceTypes: mapping.hcfType,
                  requiredBy: mapping.requiredBy,
                  requiredForBooking: mapping.requiredForBooking,
                  domain: mapping.domain,
                  isOrientationRequirement: mapping.isOrientationRequirement ?? false,
                },
                relationships: {
                  requirement: {
                    data: {
                      id: mapping.reqId,
                      type: REQUIREMENT_RESOURCE,
                    },
                  },
                },
              };

              return (
                <EditRequirementMappingDialog
                  hcfId={hcfId}
                  requirementMapping={requirementMapping}
                  onSuccess={refetch}
                />
              );
            },
          },
        ]),
    ...(hasEditPermission && !isDefined(hcfId)
      ? [
          {
            title: "Delete",
            // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter '_' implicitly has an 'any' type.
            render: (_, mapping) => {
              return (
                <Button type="link" onClick={() => setMappingToDelete(mapping)}>
                  Delete
                </Button>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <ActionContainer>{filterButtons}</ActionContainer>
      <Table
        rowKey="_id"
        size="small"
        columns={columns}
        dataSource={mappings}
        pagination={{ showSizeChanger: false }}
        loading={loading || isLoadingWorkplaceTypes || isLoadingQualifications}
        scroll={{ x: true }}
      />
      <ShowError message="Unable to load the requirement mappings" error={error} />
      <Modal
        title="Delete Mapping"
        visible={!!mappingToDelete}
        onOk={handleDeleteMapping}
        confirmLoading={isDeleting}
        onCancel={handleCancelDelete}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ disabled: isDeleting }}
      >
        <p>Are you sure you want to delete this mapping?</p>
        {mappingToDelete && <p>Name: {mappingToDelete.requirement.name}</p>}
      </Modal>
    </>
  );
}
